import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { type FormEvent, useEffect, useId, useRef, useState } from "react";
import { Button } from "../components/ui/button";
import { cn } from "../lib/utils";
import {
  compterMots,
  type Double,
  type DoubleDetail,
  type Gabarit,
  type LigneJournal,
  type Personnage,
} from "./contract";
import { compter } from "./format";
import {
  BadgeProvenance,
  BOUTON_CONTOUR,
  CHAMP,
  ErreurHistoires,
  FilHistoires,
  IntrouvableHistoires,
  LIEN_BOUTON,
  LIEN_BOUTON_PLEIN,
  LIEN_TEXTE,
  VignetteGabarit,
  ZoneIllustration,
} from "./histoires-commun";
import {
  CarteTexte,
  messageDeResultat,
  type TexteDeLaDouble,
  useTexteDeLaDouble,
} from "./histoires-editeur";
import {
  messageDEchec,
  relireLaDouble,
  restaurerLigne,
  useDouble,
  useLivre,
  useModifierDouble,
} from "./histoires-requetes";
import {
  GABARITS,
  LIBELLE_GABARIT,
  LIBELLE_MODE,
  LIBELLE_ORIGINE,
  LIBELLE_PROVENANCE,
  normaliser,
  type PlanDuLivre,
  pagesLongues,
  planDuLivre,
  quandEnClair,
  titreDouble,
} from "./histoires-textes";
import { statutDe } from "./queries";
import { ConsoleShell } from "./shell";
import { affichageDeLecture, Chargement, idValide, RelectureEchouee } from "./states";

/** Une relecture de la double refusee (401, 403) alors qu'une lecture precedente existe. */
type RefusDeRelecture = { readonly statut: number; readonly error: unknown };

/**
 * Une double (`/histoires/doubles/$id`), maquettes `HistoiresDouble.dc.html` (ordinateur) et
 * `HistoiresDoubleMobile.dc.html` (telephone) : l'intention, le texte et tout ce qui le tient
 * (`histoires-editeur.tsx`), la zone d'illustration vide et le gabarit, la composition, les
 * personnages presents, les figurants, le journal avec « Restaurer », precedente et suivante.
 *
 * Au telephone (sous `md`, amendement 2 de la spec) : l'intention, le texte, la sauvegarde, le
 * compteur, les mots interdits, precedente et suivante, rien d'autre. Le reste porte
 * `max-md:hidden` : hors de l'ecran et de l'arbre d'accessibilite.
 *
 * Le numero d'une double (« 3. Elle est fatiguée ») et ses pages ne sont pas dans la lecture de
 * la double : l'ecran lit aussi le livre, et s'en passe tant qu'il n'est pas la.
 */

export function HistoiresDoublePage({ id }: { id: string }) {
  const valide = idValide(id);
  const lecture = useDouble(id, valide);
  const affichage = affichageDeLecture(lecture);
  // Une relecture refusee (session tombee, adresse retiree de la liste) ne demonte pas l'editeur
  // d'office : s'il tient une saisie pas encore enregistree, il reste, fige et copiable (D7).
  const statut = lecture.isError ? statutDe(lecture.error) : null;
  const refus: RefusDeRelecture | null =
    lecture.data !== undefined && (statut === 401 || statut === 403)
      ? { statut, error: lecture.error }
      : null;
  const detail = affichage.contenu || refus !== null ? lecture.data : undefined;
  const livre = useLivre(detail === undefined ? "" : String(detail.livre.id), detail !== undefined);
  const plan = livre.data === undefined ? null : planDuLivre(livre.data.doubles);

  return (
    <ConsoleShell connecte={affichage.contenu}>
      {detail === undefined ? <FilHistoires /> : null}
      {!valide ? <IntrouvableHistoires quoi={`la double ${id}`} /> : null}
      {valide && lecture.isPending ? <Chargement quoi="de la double" /> : null}
      {affichage.ecranDErreur && refus === null ? (
        <ErreurHistoires error={lecture.error} quoi={`la double ${id}`} />
      ) : null}
      {affichage.bandeau ? <RelectureEchouee error={lecture.error} /> : null}
      {detail === undefined ? null : (
        <EcranDouble detail={detail} key={detail.double.id} plan={plan} refus={refus} />
      )}
    </ConsoleShell>
  );
}

/** « Double 3 sur 16 · pages 8 et 9 », « page 1 », ou rien tant que le livre n'est pas lu. */
export function sousTitreDouble(
  double: Pick<Double, "id" | "type">,
  plan: PlanDuLivre | null,
): string {
  if (plan === null) return "";
  const pages = plan.pages.get(double.id) ?? null;
  const numero = plan.numeros.get(double.id);
  const morceaux = [
    double.type === "histoire" && numero !== undefined
      ? `Double ${numero} sur ${plan.histoires}`
      : null,
    pages === null
      ? double.type === "couverture"
        ? "hors des pages du livre"
        : null
      : pagesLongues(pages),
  ];
  return morceaux.filter((morceau) => morceau !== null).join(" · ");
}

function EcranDouble({
  detail,
  plan,
  refus,
}: {
  detail: DoubleDetail;
  plan: PlanDuLivre | null;
  refus: RefusDeRelecture | null;
}) {
  const client = useQueryClient();
  const { livre, double, journal, precedente, suivante, personnages_du_livre } = detail;
  const titre = titreDouble(double, plan?.numeros.get(double.id));
  const texte = useTexteDeLaDouble(double);
  const sousTitre = sousTitreDouble(double, plan);
  const titreVoisine = (voisine: NonNullable<DoubleDetail["precedente"]>): string =>
    titreDouble(voisine, plan?.numeros.get(voisine.id));

  // Une saisie attend (pas encore enregistree, ou arretee par un 409) : un refus de relecture
  // garde l'editeur jusqu'a ce que la double se relise.
  const attend = texte.moteur.occupe() || texte.instantane.etat.type === "conflit";
  const [garderLEditeur, setGarderLEditeur] = useState(false);
  useEffect(() => {
    if (refus === null) setGarderLEditeur(false);
    else if (attend) setGarderLEditeur(true);
  }, [refus, attend]);
  // Reconnecte ailleurs, « Réessayer » a enregistre la saisie : la double se relit.
  const enregistre = texte.instantane.etat.type === "enregistre";
  useEffect(() => {
    if (refus !== null && enregistre) relireLaDouble(client, double.id);
  }, [refus, enregistre, client, double.id]);

  if (refus !== null && !attend && !garderLEditeur) {
    return <ErreurHistoires error={refus.error} quoi={`la double ${double.id}`} />;
  }

  return (
    <>
      <FilHistoires livre={{ id: livre.id, titre: livre.titre }}>{titre}</FilHistoires>
      <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold md:text-2xl">{titre}</h1>
          {sousTitre === "" ? null : (
            <p className="mt-1 text-sm text-muted-foreground">{sousTitre}</p>
          )}
        </div>
        {/* Les memes liens qu'en bas de page, a portee de souris : hors du clavier et muets, la
            navigation du bas les porte pour tout le monde. */}
        <div aria-hidden="true" className="flex gap-2 max-md:hidden">
          {precedente === null ? null : (
            <Link
              className={LIEN_BOUTON}
              params={{ id: String(precedente.id) }}
              tabIndex={-1}
              to="/histoires/doubles/$id"
            >
              ‹ {titreVoisine(precedente)}
            </Link>
          )}
          {suivante === null ? null : (
            <Link
              className={LIEN_BOUTON}
              params={{ id: String(suivante.id) }}
              tabIndex={-1}
              to="/histoires/doubles/$id"
            >
              {titreVoisine(suivante)} ›
            </Link>
          )}
        </div>
      </header>

      <Intention double={double} />

      <div className="grid items-start gap-5 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] xl:grid-cols-[minmax(0,37.5rem)_minmax(0,1fr)]">
        <CarteTexte
          brief={livre.brief}
          double={double}
          journal={journal}
          livreId={livre.id}
          refusDeRelecture={refus === null ? null : refus.statut}
          texte={texte}
          titre={titre}
        />
        <CarteIllustration double={double} />
      </div>

      <PourLIllustration double={double} personnages={personnages_du_livre} />

      <Journal double={double} journal={journal} texte={texte} />

      <nav
        aria-label="Doubles voisines"
        className="grid grid-cols-2 gap-2.5 md:flex md:justify-between"
      >
        {precedente === null ? (
          <span />
        ) : (
          <Link
            aria-label={`Double précédente : ${titreVoisine(precedente)}`}
            className={cn(LIEN_BOUTON, "flex-col items-start gap-0.5 md:flex-row md:items-center")}
            params={{ id: String(precedente.id) }}
            to="/histoires/doubles/$id"
          >
            <span className="text-xs text-muted-foreground md:hidden">‹ Précédente</span>
            <span>
              <span className="max-md:hidden">‹ </span>
              {titreVoisine(precedente)}
            </span>
          </Link>
        )}
        {suivante === null ? null : (
          <Link
            aria-label={`Double suivante : ${titreVoisine(suivante)}`}
            className={cn(
              LIEN_BOUTON_PLEIN,
              "flex-col items-end gap-0.5 text-right md:flex-row md:items-center",
            )}
            params={{ id: String(suivante.id) }}
            to="/histoires/doubles/$id"
          >
            <span className="text-xs opacity-80 md:hidden">Suivante ›</span>
            <span>
              {titreVoisine(suivante)}
              <span className="max-md:hidden"> ›</span>
            </span>
          </Link>
        )}
      </nav>
    </>
  );
}

// ---------------------------------------------------------------------------------------
// L'intention.

function Intention({ double }: { double: Double }) {
  const modifier = useModifierDouble(double.id);
  const [edition, setEdition] = useState<"lecture" | "edition" | "fermee">("lecture");
  const [valeur, setValeur] = useState(double.intention ?? "");
  const bouton = useRef<HTMLButtonElement>(null);
  const champ = useRef<HTMLTextAreaElement>(null);
  const id = useId();

  useEffect(() => {
    if (edition === "edition") champ.current?.focus();
    if (edition === "fermee") bouton.current?.focus();
  }, [edition]);

  const enregistrer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    if (modifier.isPending) return;
    modifier.mutate(
      { intention: valeur.trim() === "" ? null : valeur },
      { onSuccess: () => setEdition("fermee") },
    );
  };

  return (
    <section
      aria-labelledby="histoires-intention"
      className="rounded-[10px] border border-border bg-muted/50 px-4 py-3.5 md:px-[18px]"
    >
      <div className="flex items-center justify-between gap-3">
        <h2
          className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
          id="histoires-intention"
        >
          Intention
        </h2>
        {edition === "edition" ? null : (
          <button
            aria-label="Modifier l'intention"
            className={cn(LIEN_TEXTE, "text-sm max-md:hidden")}
            onClick={() => {
              setValeur(double.intention ?? "");
              setEdition("edition");
            }}
            ref={bouton}
            type="button"
          >
            Modifier
          </button>
        )}
      </div>
      {edition === "edition" ? (
        <form className="mt-2 flex flex-col gap-2" onSubmit={enregistrer}>
          <label className="sr-only" htmlFor={id}>
            Intention de la double
          </label>
          <textarea
            className={cn(CHAMP, "min-h-20")}
            id={id}
            maxLength={2000}
            onChange={(evenement) => setValeur(evenement.target.value)}
            ref={champ}
            value={valeur}
          />
          {modifier.isError ? (
            <p className="text-sm text-destructive" role="alert">
              {messageDEchec(modifier.error)}
            </p>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" type="submit">
              Enregistrer l'intention
            </Button>
            <button
              className={cn(BOUTON_CONTOUR, "min-h-8 px-3 py-1")}
              onClick={() => setEdition("fermee")}
              type="button"
            >
              Annuler
            </button>
          </div>
        </form>
      ) : double.intention === null || double.intention.trim() === "" ? (
        <p className="mt-1.5 text-sm text-muted-foreground">Pas encore d'intention.</p>
      ) : (
        <p className="mt-1.5 whitespace-pre-line text-sm leading-relaxed md:text-[15px]">
          {double.intention}
        </p>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------------------
// L'illustration et le gabarit.

function CarteIllustration({ double }: { double: Double }) {
  const modifier = useModifierDouble(double.id);
  const histoire = double.type === "histoire";
  return (
    <section
      aria-labelledby="histoires-illustration"
      className="flex min-w-0 flex-col gap-3 rounded-lg border border-border bg-card p-5 text-card-foreground max-md:hidden"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold" id="histoires-illustration">
          Illustration
        </h2>
        <span className="text-xs text-muted-foreground">pas encore d'illustration</span>
      </div>
      <ZoneIllustration gabarit={double.gabarit} grande />
      <p className="text-xs text-muted-foreground">
        {double.gabarit === null
          ? histoire
            ? "Choisis un gabarit : la place de son texte se pose ici, en pointillé, avant la première image."
            : "Cette page a un gabarit fixe : pas de choix à faire."
          : "En pointillé : la place du texte du gabarit, posée sur l'illustration quand elle sera choisie. Le pli ne porte ni texte ni visage."}
      </p>
      {histoire ? (
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-2 text-sm font-semibold">Gabarit</legend>
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
            {GABARITS.map((gabarit: Gabarit) => (
              <button
                aria-pressed={double.gabarit === gabarit}
                className={cn(
                  "flex flex-col items-start gap-1.5 rounded-lg border bg-card p-2 text-left text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50",
                  double.gabarit === gabarit
                    ? "border-foreground font-semibold ring-1 ring-foreground"
                    : "border-input hover:border-foreground/70",
                )}
                disabled={modifier.isPending}
                key={gabarit}
                onClick={() => {
                  if (double.gabarit !== gabarit) modifier.mutate({ gabarit });
                }}
                type="button"
              >
                <VignetteGabarit gabarit={gabarit} />
                {LIBELLE_GABARIT[gabarit]}
              </button>
            ))}
          </div>
          {modifier.isError ? (
            <p className="text-sm text-destructive" role="alert">
              {messageDEchec(modifier.error)}
            </p>
          ) : null}
        </fieldset>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------------------
// Pour l'illustration : composition, personnages, figurants.

function PourLIllustration({
  double,
  personnages,
}: {
  double: Double;
  personnages: readonly Personnage[];
}) {
  const modifier = useModifierDouble(double.id);
  const basculer = useModifierDouble(double.id);
  const serveurComposition = double.composition ?? "";
  const serveurFigurants = double.figurants ?? "";
  const [composition, setComposition] = useState(serveurComposition);
  const [figurants, setFigurants] = useState(serveurFigurants);
  const [enregistre, setEnregistre] = useState(false);
  const id = useId();
  const change = composition !== serveurComposition || figurants !== serveurFigurants;

  // Une relecture qui rend une autre composition (Claude l'a posee par `histoire console
  // composer`) : prise dans un champ qu'on n'a pas touche, laissee sinon. Enregistrer la saisie
  // en cours l'emporte alors, comme pour tout champ qu'on vient d'ecrire.
  const precedent = useRef({ composition: serveurComposition, figurants: serveurFigurants });
  useEffect(() => {
    const avant = precedent.current;
    precedent.current = { composition: serveurComposition, figurants: serveurFigurants };
    setComposition((locale) => (locale === avant.composition ? serveurComposition : locale));
    setFigurants((locale) => (locale === avant.figurants ? serveurFigurants : locale));
  }, [serveurComposition, serveurFigurants]);

  const enregistrer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    if (!change || modifier.isPending) return;
    setEnregistre(false);
    modifier.mutate(
      {
        composition: composition.trim() === "" ? null : composition,
        figurants: figurants.trim() === "" ? null : figurants,
      },
      { onSuccess: () => setEnregistre(true) },
    );
  };

  const presents = new Set(double.personnages);
  const basculerPersonnage = (personnageId: number): void => {
    const suivants = presents.has(personnageId)
      ? double.personnages.filter((autre) => autre !== personnageId)
      : [...double.personnages, personnageId];
    basculer.mutate({ personnages: suivants });
  };

  return (
    <section
      aria-labelledby="histoires-pour-illustration"
      className="rounded-lg border border-border bg-card p-5 text-card-foreground max-md:hidden"
    >
      <h2 className="mb-3.5 text-base font-semibold" id="histoires-pour-illustration">
        Pour l'illustration
      </h2>
      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)]">
        <form className="flex flex-col gap-3" onSubmit={enregistrer}>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold" htmlFor={`${id}-composition`}>
              Composition <span className="font-normal text-muted-foreground">en anglais</span>
            </label>
            <textarea
              aria-describedby={`${id}-composition-aide`}
              className={cn(CHAMP, "min-h-28 leading-relaxed")}
              id={`${id}-composition`}
              lang="en"
              maxLength={4000}
              onChange={(evenement) => {
                setComposition(evenement.target.value);
                setEnregistre(false);
              }}
              value={composition}
            />
            <p className="text-xs text-muted-foreground" id={`${id}-composition-aide`}>
              La scène, et la place du texte décrite comme un morceau du décor. Claude la pose par{" "}
              <code className="font-mono">histoire console composer</code>, ou tu l'écris ici.
            </p>
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold" htmlFor={`${id}-figurants`}>
              Figurants{" "}
              <span className="font-normal text-muted-foreground">
                en anglais, décrits : tenue, taille
              </span>
            </label>
            <input
              className={CHAMP}
              id={`${id}-figurants`}
              lang="en"
              maxLength={2000}
              onChange={(evenement) => {
                setFigurants(evenement.target.value);
                setEnregistre(false);
              }}
              placeholder="aucun"
              value={figurants}
            />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button disabled={!change} size="sm" type="submit" variant="outline">
              Enregistrer la composition
            </Button>
            {enregistre && !change ? (
              <span className="text-xs text-muted-foreground" role="status">
                Enregistrée.
              </span>
            ) : null}
          </div>
          {modifier.isError ? (
            <p className="text-sm text-destructive" role="alert">
              {messageDEchec(modifier.error)}
            </p>
          ) : null}
        </form>
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-2 text-sm font-semibold">Personnages présents</legend>
          {personnages.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Le livre n'a pas encore de personnage : ils s'ajoutent dans l'onglet Personnages.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {personnages.map((personnage) => {
                const present = presents.has(personnage.id);
                return (
                  <button
                    aria-pressed={present}
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50",
                      present
                        ? "border-foreground bg-foreground text-background"
                        : "border-input bg-card text-muted-foreground hover:border-foreground/70",
                    )}
                    disabled={basculer.isPending}
                    key={personnage.id}
                    onClick={() => basculerPersonnage(personnage.id)}
                    type="button"
                  >
                    {present ? <span aria-hidden="true">✓</span> : null}
                    {personnage.nom}
                  </button>
                );
              })}
            </div>
          )}
          {basculer.isError ? (
            <p className="text-sm text-destructive" role="alert">
              {messageDEchec(basculer.error)}
            </p>
          ) : null}
        </fieldset>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------------------
// Le journal du texte.

function Journal({
  double,
  journal,
  texte: { moteur, instantane },
}: {
  double: Double;
  journal: readonly LigneJournal[];
  texte: TexteDeLaDouble;
}) {
  const client = useQueryClient();
  const [ouverte, setOuverte] = useState<number | null>(null);
  const [envoi, setEnvoi] = useState<number | null>(null);
  const [retour, setRetour] = useState<{ ton: "ok" | "echec"; texte: string } | null>(null);
  const maintenant = Date.now();
  // Une saisie en attente, un 409, une autre ecriture de l'ecran (repartir de zero, brouillon a
  // reprendre) : restaurer passerait par-dessus. Le bouton attend.
  const occupe =
    moteur.occupe() ||
    instantane.etat.type === "conflit" ||
    envoi !== null ||
    instantane.verrouille;

  const restaurer = async (ligne: LigneJournal): Promise<void> => {
    if (occupe) return;
    setEnvoi(ligne.id);
    setRetour(null);
    // Pendant la restauration, la zone est figee : rien ne s'y tape que le texte restaure ecraserait.
    moteur.verrouiller("restauration");
    try {
      const resultat = await restaurerLigne(double.id, ligne.id, moteur.lire().version);
      if (resultat.type === "ok") {
        // Refuse si une saisie attendait malgre tout : la prochaine ecriture fera le 409, qui la garde.
        moteur.remplacer(
          normaliser(ligne.texte_apres),
          resultat.reponse.texte_version,
          resultat.reponse.provenance,
        );
        relireLaDouble(client, double.id);
        setRetour({
          ton: "ok",
          texte: `Texte restauré : celui de ${quandEnClair(ligne.maj_le, maintenant)}.`,
        });
        return;
      }
      if (resultat.type === "conflit") {
        moteur.signalerConflit(resultat.courant);
        return;
      }
      setRetour({ ton: "echec", texte: messageDeResultat(resultat) });
    } finally {
      setEnvoi(null);
      moteur.deverrouiller("restauration");
    }
  };

  return (
    <section
      aria-labelledby="histoires-journal"
      className="rounded-lg border border-border bg-card p-5 text-card-foreground max-md:hidden"
    >
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold" id="histoires-journal">
          Journal du texte
        </h2>
        <span className="text-xs text-muted-foreground">
          tes saisies à moins de 10 minutes d'écart se fondent en une ligne
        </span>
      </div>
      {journal.length === 0 ? (
        <p className="text-sm text-muted-foreground">Pas encore d'écriture sur cette double.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <caption className="sr-only">Les écritures du texte, la plus récente d'abord</caption>
            <thead>
              <tr className="text-left text-xs text-muted-foreground">
                <th className="px-3 py-2 font-semibold" scope="col">
                  Quand
                </th>
                <th className="px-3 py-2 font-semibold" scope="col">
                  Qui
                </th>
                <th className="px-3 py-2 font-semibold" scope="col">
                  Quoi
                </th>
                <th className="px-3 py-2 font-semibold" scope="col">
                  Provenance
                </th>
                <th className="px-3 py-2 text-right font-semibold" scope="col">
                  Mots
                </th>
                <th className="px-3 py-2" scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {journal.map((ligne, rang) => (
                <LigneDuJournal
                  actuelle={rang === 0}
                  key={ligne.id}
                  ligne={ligne}
                  maintenant={maintenant}
                  occupe={occupe}
                  ouverte={ouverte === ligne.id}
                  restaurer={() => {
                    void restaurer(ligne);
                  }}
                  enCours={envoi === ligne.id}
                  voir={() => setOuverte(ouverte === ligne.id ? null : ligne.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {retour === null ? null : (
        <p
          className={cn("mt-2 text-sm", retour.ton === "echec" && "text-destructive")}
          role={retour.ton === "echec" ? "alert" : "status"}
        >
          {retour.texte}
        </p>
      )}
      <p className="mt-2.5 text-xs text-muted-foreground">
        Restaurer rend le texte et la provenance de la ligne : une saisie à toi redonne un texte «
        écrit ». La restauration a sa propre ligne au journal, et ce qu'elle remplace reste
        restaurable.
      </p>
    </section>
  );
}

function ProvenanceDeLaLigne({ ligne }: { ligne: LigneJournal }) {
  const memes = ligne.provenance_avant === ligne.provenance_apres;
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <BadgeProvenance provenance={ligne.provenance_avant} />
      <span className="text-xs text-muted-foreground">{memes ? "reste" : "→"}</span>
      <BadgeProvenance provenance={ligne.provenance_apres} />
    </span>
  );
}

function LigneDuJournal({
  ligne,
  actuelle,
  ouverte,
  occupe,
  enCours,
  maintenant,
  voir,
  restaurer,
}: {
  ligne: LigneJournal;
  actuelle: boolean;
  ouverte: boolean;
  occupe: boolean;
  enCours: boolean;
  maintenant: number;
  voir: () => void;
  restaurer: () => void;
}) {
  const quand = quandEnClair(ligne.maj_le, maintenant);
  const cellule = "border-t border-border px-3 py-2.5 align-middle";
  const detail = (
    etiquette: string,
    texte: string | null,
    provenance: LigneJournal["provenance_avant"],
  ) => (
    <div className="rounded-md border border-border bg-muted/50 px-3 py-2.5">
      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {etiquette} · {provenance === null ? "vide" : LIBELLE_PROVENANCE[provenance]},{" "}
        {compter(compterMots(texte), "mot", "mots")}
      </p>
      <p className="whitespace-pre-line text-sm">
        {texte === null || texte === "" ? "(pas de texte)" : texte}
      </p>
    </div>
  );
  return (
    <>
      <tr>
        <td className={cellule}>{quand}</td>
        <td className={cellule}>{LIBELLE_ORIGINE[ligne.origine]}</td>
        <td className={cellule}>{LIBELLE_MODE[ligne.mode]}</td>
        <td className={cellule}>
          <ProvenanceDeLaLigne ligne={ligne} />
        </td>
        <td className={cn(cellule, "text-right tabular-nums")}>{compterMots(ligne.texte_apres)}</td>
        <td className={cn(cellule, "whitespace-nowrap text-right")}>
          {actuelle ? (
            <span className="text-xs text-muted-foreground">texte actuel</span>
          ) : (
            <span className="inline-flex items-center gap-2.5">
              <button
                aria-expanded={ouverte}
                aria-label={`${ouverte ? "Masquer" : "Voir"} la ligne de ${quand}`}
                className={cn(LIEN_TEXTE, "text-sm")}
                onClick={voir}
                type="button"
              >
                {ouverte ? "Masquer" : "Voir"}
              </button>
              <button
                aria-label={`Restaurer le texte de ${quand}`}
                className={cn(BOUTON_CONTOUR, "min-h-8 px-3 py-1")}
                disabled={occupe}
                onClick={restaurer}
                type="button"
              >
                {enCours ? "Restauration..." : "Restaurer"}
              </button>
            </span>
          )}
        </td>
      </tr>
      {ouverte && !actuelle ? (
        <tr>
          <td className="px-3 pb-3" colSpan={6}>
            <div className="grid gap-3 md:grid-cols-2">
              {detail("Avant", ligne.texte_avant, ligne.provenance_avant)}
              {detail("Après", ligne.texte_apres, ligne.provenance_apres)}
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}
